import PageHeader from '@/components/PageHeader'
import StatusBadge from '@/components/StatusBadge'
import {
  SECRET_STORE_REGISTRY,
  SECRET_STORE_TYPE_LABELS,
  SYSTEM_LABELS,
  type LifecycleFlag,
  type SecretStoreEntry,
  type SecretStoreType,
} from '@/data/secretStoreRegistry'

const FLAG_META: Record<LifecycleFlag, { label: string; tone: 'danger' | 'warning' | 'info' }> = {
  'plaintext-risk': { label: 'plaintext risk', tone: 'danger' },
  'rotation-due': { label: 'rotation due', tone: 'warning' },
  'url-drift': { label: 'url drift', tone: 'warning' },
  'has-ttl': { label: 'has TTL', tone: 'info' },
}

const TYPE_ORDER: SecretStoreType[] = [
  'hashicorp-vault',
  'k8s-secret',
  'vaultwarden',
  'age-file',
  'gitea-ci',
  'maestro-file-vault',
  'env-file',
]

function SecretStoreRow({ entry }: { entry: SecretStoreEntry }) {
  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-2 whitespace-nowrap">
        <StatusBadge tone="neutral" label={SYSTEM_LABELS[entry.system]} />
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {entry.location}
        {entry.referencedAt ? (
          <div className="text-muted-foreground/60">{entry.referencedAt}</div>
        ) : null}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {entry.keys.map((k) => (
            <span
              key={k}
              className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
            >
              {k}
            </span>
          ))}
        </div>
        {entry.note ? <p className="mt-1 text-[11px] text-muted-foreground">{entry.note}</p> : null}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <StatusBadge tone="info" label={entry.auth} />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {(entry.flags ?? []).map((f) => (
            <StatusBadge key={f} tone={FLAG_META[f].tone} label={FLAG_META[f].label} />
          ))}
        </div>
      </td>
    </tr>
  )
}

export default function SecretStores() {
  const total = SECRET_STORE_REGISTRY.length
  const plaintextRisks = SECRET_STORE_REGISTRY.filter((e) =>
    e.flags?.includes('plaintext-risk'),
  ).length

  const groups = TYPE_ORDER.map((type) => ({
    type,
    entries: SECRET_STORE_REGISTRY.filter((e) => e.type === type),
  })).filter((g) => g.entries.length > 0)

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Secret Stores"
        description={`Fleet inventory of ${total} secret stores across Maestro, MOS, Closer, and Marvin (ADR-0017). Names and categories only — never values.`}
      />

      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-400">
        Canonical Vault is <code>http://10.99.0.1:8200</code>. <code>10.0.0.1</code> is
        decommissioned and <code>127.0.0.1</code> is an operator SSH-tunnel convenience — both are
        flagged <em>url drift</em> where still referenced. {plaintextRisks} store
        {plaintextRisks === 1 ? '' : 's'} carry a <em>plaintext risk</em> flag and should move
        behind Vault.
      </div>

      {groups.map(({ type, entries }) => (
        <section key={type} className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {SECRET_STORE_TYPE_LABELS[type]}{' '}
            <span className="text-muted-foreground/60">({entries.length})</span>
          </h3>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">System</th>
                  <th className="px-3 py-2 font-medium">Location</th>
                  <th className="px-3 py-2 font-medium">Keys / categories</th>
                  <th className="px-3 py-2 font-medium">Auth</th>
                  <th className="px-3 py-2 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => (
                  <SecretStoreRow key={`${type}-${i}`} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  )
}
